import React, { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { Droplets, Mail, Lock, Loader2 } from 'lucide-react'

// Traduce los errores más comunes de Supabase Auth al español
const translateAuthError = (message) => {
  const map = {
    'Invalid login credentials': 'Correo o contraseña incorrectos',
    'Email not confirmed': 'Tu correo aún no ha sido confirmado',
    'User not found': 'No existe una cuenta con ese correo',
  }
  return map[message] || message
}

const LoginComponent = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const navigate = useNavigate()
  const { session } = useAuth()

  // Redirect if already authenticated
  useEffect(() => {
    if (session) {
      navigate('/dashboard')
    }
  }, [session, navigate])

  const handleLogin = async (e) => {
    e.preventDefault()
    setErrorMessage('')
    setLoading(true)

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password })

      if (authError) {
        setErrorMessage(translateAuthError(authError.message))
      } else if (data.session) {
        navigate('/dashboard')
      } else {
        setErrorMessage('El inicio de sesión fue exitoso pero no se recibió una sesión.')
      }
    } catch (err) {
      setErrorMessage('Error inesperado: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-page relative flex items-center justify-center min-h-screen overflow-hidden px-4 py-8">
      {/* Animaciones (auto-contenidas, sin dependencias externas) */}
      <style>{`
        @keyframes login-gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes login-blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -40px) scale(1.1); }
          66% { transform: translate(-20px, 30px) scale(0.95); }
        }
        @keyframes login-fade-up {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes login-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .login-page {
          background: linear-gradient(-45deg, #0c4a6e, #0369a1, #0891b2, #2563eb);
          background-size: 400% 400%;
          animation: login-gradient 16s ease infinite;
        }
        .login-blob {
          position: absolute;
          border-radius: 9999px;
          filter: blur(64px);
          opacity: 0.5;
          pointer-events: none;
        }
        .login-card {
          animation: login-fade-up 0.6s ease-out both;
        }
        .login-logo { animation: login-float 4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .login-page, .login-blob, .login-card, .login-logo { animation: none; }
        }
      `}</style>

      {/* Blobs decorativos animados */}
      <div className="login-blob" style={{ width: 380, height: 380, background: '#38bdf8', top: '-80px', left: '-60px', animation: 'login-blob 14s ease-in-out infinite' }} />
      <div className="login-blob" style={{ width: 320, height: 320, background: '#22d3ee', bottom: '-70px', right: '-50px', animation: 'login-blob 18s ease-in-out infinite reverse' }} />
      <div className="login-blob" style={{ width: 260, height: 260, background: '#818cf8', top: '40%', left: '60%', animation: 'login-blob 20s ease-in-out infinite' }} />

      {/* Tarjeta de login */}
      <div className="login-card relative z-10 bg-white/90 backdrop-blur-xl shadow-2xl rounded-2xl px-5 sm:px-8 py-7 sm:py-9 w-full max-w-md border border-white/40">
        <div className="text-center mb-6 sm:mb-8">
          <div className="login-logo inline-flex items-center justify-center w-16 h-16 mb-4 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 shadow-lg shadow-blue-500/30">
            <Droplets className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">Aqualiquim</h2>
          <p className="text-gray-500 text-sm sm:text-base">Inicia sesión en tu cuenta</p>
        </div>

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-3 rounded-lg mb-4 sm:mb-6 text-xs sm:text-sm">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
          <div>
            <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
              Correo electrónico
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                id="email"
                type="email"
                placeholder="tu@correo.com"
                className="w-full pl-10 pr-3 sm:pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700">
                Contraseña
              </label>
              <Link
                to="/forgot-password"
                className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                id="password"
                type="password"
                placeholder="••••••••"
                className="w-full pl-10 pr-3 sm:pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 active:scale-[0.98] text-white font-semibold py-3 px-4 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-xl hover:shadow-blue-500/30"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            ¿Necesitas una cuenta?{' '}
            <span className="text-gray-500">
              Contacta al administrador para solicitar una invitación
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginComponent
